import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import DatePicker from '../rangepicker/rangepicker';
import { DatePickerMode, RangeOutputType } from '../rangepicker/types';
import IconCalendar from '../icons/calendar';
import IconArrowRight from '../icons/arrow-right';
import formatDate from '../../helpers/format-date';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import { DateInputProps, PickerPosition } from './types';
import usePrevious from '../../hooks/use-previous';

import styles from './date-input.module.css';

export default function DateInputRange({
  placeholder,
  values,
  pickerPisition = PickerPosition.LEFT,
  onChangeRange,
  error,
  className = '',
  bold,
  withoutCustomSelector,
  withoutTwoPickers,
  disabledAfter = new Date(2099, 0, 1),
  title,
  withTitle,
  onReset,
  withResetClose,
  dateInputTitle,
  disabled,
}: DateInputProps): React.JSX.Element {
  const [inputValues, setInputValues] = useState<string[] | undefined>(values);
  const [datepickerVisibe, setDatepickerVisible] = useState(false);

  const handleApply = (result: RangeOutputType) => {
    setInputValues(result.map((item) => new Date(item as Date)?.toISOString()));
    setDatepickerVisible(false);

    if (onChangeRange) {
      onChangeRange(result as Date[]);
    }
  };

  const prevDatepickerVisible = usePrevious(datepickerVisibe);

  useEffect(() => {
    if (!inputValues && values) {
      setInputValues(values);
    } else if (inputValues && !values) {
      setInputValues(undefined);
    }
  }, [inputValues, values]);

  useEffect(() => {
    if (!datepickerVisibe && prevDatepickerVisible) {
      setInputValues([]);
    }
  }, [datepickerVisibe, prevDatepickerVisible]);

  useEffect(() => {
    if (inputValues && values && !isEqual(inputValues, values)) {
      setInputValues(values);
    }
  }, [inputValues, values]);

  let buttonContent;

  if (!inputValues?.length) {
    buttonContent = placeholder;
  } else {
    buttonContent = (
      <span>
        {formatDate(inputValues[0])}
        {inputValues.length > 1 && (
          <>
            <IconArrowRight width={10} height={10} />
            {formatDate(inputValues[1])}
          </>
        )}
      </span>
    );

    if (new Date(inputValues[0]).getFullYear() === 1970) {
      buttonContent = <span>For all time</span>;
    }
  }

  return (
    <div>
      {dateInputTitle && (
        <div className={styles.dateInputTitle}>{dateInputTitle}</div>
      )}
      <div
        className={`${styles.dateInputWrapper} ${className} ${
          bold ? styles.bold : ''
        }`}
      >
        <button
          type="button"
          className={styles.dateInputRangeButton}
          onClick={() => setDatepickerVisible(!datepickerVisibe)}
          disabled={disabled}
        >
          <div className={styles.dateInputValues}>{buttonContent}</div>
          <IconCalendar width={17} height={17} />
        </button>
        {error && <div className={styles.dateInputError}>{error}</div>}
        {datepickerVisibe && (
          <ClickOutsideDetector setOptionsListVisible={setDatepickerVisible}>
            <div
              className={`${styles.dateInputPicker} ${
                styles.dateInputPickerFullWidth
              } ${
                pickerPisition === PickerPosition.RIGHT
                  ? styles.dateInputPickerRight
                  : styles.dateInputPickerLeft
              }`}
            >
              <DatePicker
                startDate={new Date(1920, 0, 1)}
                endDate={new Date(2099, 0, 1)}
                onApply={handleApply}
                onReset={onReset}
                mode={DatePickerMode.RANGE}
                value={inputValues}
                twoPickers={!withoutTwoPickers}
                disabledAfter={disabledAfter}
                withoutCustomSelector={withoutCustomSelector}
                prevDatepickerVisible={prevDatepickerVisible}
                datepickerVisible={datepickerVisibe}
                title={title}
                withTitle={withTitle}
                withResetClose={withResetClose}
                setDatepickerVisible={setDatepickerVisible}
              />
            </div>
          </ClickOutsideDetector>
        )}
      </div>
    </div>
  );
}
